import React, { useState, useEffect } from 'react';

import {
  Grid,
  Typography,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  Alert,
} from '@mui/material';

import RegularTemplateEditor from './RegularTemplateEditor';
import MessageNode from "./MessageNode";
import CarouselTemplateEditor from './CarouselTemplateEditor';

import axios from 'axios';

import { TEMPLATE_CREATE_ENDPOINT } from '../../config/urls';
import { useAuth } from '../../hooks/AuthProvider';

const SetupTemplate = (props) => {
    const { getValidToken } = useAuth();
    const [templateOps, setTemplateOps] = useState('setup-template');
    const [templateType, setTemplateType] = useState('');
    const [marketingOption, setMarketingOption] = useState('');
    const [utilityOption, setUtilityOption] = useState('');
    const templateSteps = ['setup-template', 'edit-template'];

    const [headerDuplicatePlaceholderError, setHeaderDuplicatePlaceholderError] = useState(false);
    const [bodyDuplicatePlaceholderError, setBodyDuplicatePlaceholderError] = useState(false);

    const [carouselMainBodyDuplicatePlaceholderError, setCarouselMainBodyDuplicatePlaceholderError] = useState(false);
    const [carouselCardBodyDuplicatePlaceholderError, setCarouselCardBodyDuplicatePlaceholderError] = useState([]);

    const { templatesNamesData, setComponent } = props;

    const [carouselType, setCarouselType] = useState('image');
    const [carouselMainBodySampleFields, setCarouselMainBodySampleFields] = useState([]);

    const columns = [
        { id: 'header', label: 'Header' },
        { id: 'body', label: 'Body' },
        { id: 'footer', label: 'Footer' },
    ];

    // edit template form
    const [templateName, setTemplateName] = useState('');
    const [language, setLanguage] = useState('english');
    const [headerType, setHeaderType] = useState('none');
    const [headerText, setHeaderText] = useState('');
    const [headerFile, setHeaderFile] = useState(null);
    const [body, setBody] = useState('');
    const [sampleBodyFields, setsampleBodyFields] = useState([]);
    const [sampleHeaderFields, setsampleHeaderFields] = useState([]);
    const [footer, setFooter] = useState('');
    const [allButtons, setAllButtons] = useState([]);
    const [isButtonsValid, setIsButtonsValid] = useState(true);
    const [error, setError] = useState(false);

    const [carouselMainBody, setCarouselMainBody] = useState('');
    const [carouselCards, setCarouselCards] = useState([
        {
        id: Date.now(), // Add a unique ID to help React distinguish between cards
        // headerType: 'IMAGE',
        headerType: carouselType.toUpperCase(),
        headerFile: null,
        body: '',
        sampleBodyFields: [],
        buttons: [
            {
            type: 'QUICK_REPLY',
            data: {
                buttonText: ''
            }
            }
        ]
        }
    ]);

    const [formData, setFormData] = useState({
        templateName: '',
        language: '',
        headerType: '',
        headerText: '',
        headerFile: '',
        sampleHeaderFields: [],
        body: '',
        sampleBodyFields: [],
        footer: '',
        marketingOption: '',
        templateType: '',
        allButtons: [],
        carouselMainBodySampleFields: [],
    });

    const [alertObj, setAlertObj] = useState({
        active: false,
        msg: "",
        type: "",
        statusCode: ""
    });

    const handleSuccessAlert = () => {
        setAlertObj({
        active: true,
        type: "success",
        msg: "Template submitted successfully",
        statusCode: 201
        });
        setComponent(1);
    };
      
    const handleErrorAlert = (statusCode, responseText) => {
        setAlertObj({
        active: true,
        type: "error",
        msg: `Failed to submit template: ${responseText}`,
        statusCode: statusCode
        });
    };

    const handleAlertClose = () => {
        setAlertObj({
        active: false,
        type: "",
        msg: "",
        statusCode: ""
        });
    };

    useEffect(() => {
        setFormData({
        templateName,
        language,
        headerType,
        headerText,
        headerFile,
        sampleHeaderFields,
        body,
        sampleBodyFields,
        footer,
        marketingOption,
        templateType,
        allButtons,
        carouselCards,
        carouselMainBody,
        carouselMainBodySampleFields,
        carouselType
        });
    }, [templateName, language, headerType, headerText, headerFile, sampleHeaderFields, body, sampleBodyFields, footer, marketingOption, templateType, allButtons, carouselCards, carouselMainBody, carouselMainBodySampleFields, carouselType]);

    const handleSubmitData = async(event) => {

        const apiUrl = TEMPLATE_CREATE_ENDPOINT;

        // Prevent submit if there are duplicate placeholder errors
        if (
            headerDuplicatePlaceholderError ||
            bodyDuplicatePlaceholderError ||
            carouselMainBodyDuplicatePlaceholderError ||
            carouselCardBodyDuplicatePlaceholderError.some(Boolean)
        ) {
            setAlertObj({ active: true, type: "error", msg: "Please resolve the parameter errors in the form" });
            return;
        }
    
        // If it's a carousel template, check if each card has a required media and body
        if (formData.marketingOption === 'carousel') {
            if (formData.carouselCards.length === 0) {
                setAlertObj({ active: true, type: "error", msg: "Please add at least one card" });
                return;
            }

            // Check if main body is empty
            if (!formData.carouselMainBody || formData.carouselMainBody.trim() === '') {
                setAlertObj({ active: true, type: "error", msg: "Please enter a main message body for the template" });
                return;
            }
            
            // Validate each card
            for (let i = 0; i < formData.carouselCards.length; i++) {
                const card = formData.carouselCards[i];
                if (!card.headerFile) {
                    setAlertObj({ 
                        active: true, 
                        type: "error", 
                        msg: `Please add ${formData.carouselType === 'video' ? 'a video' : 'an image'} for Card ${i+1}` 
                    });
                    return;
                }
                
                // For video files, add additional validation if needed
                if (formData.carouselType === 'video' && card.headerFile) {
                    // Optional: Add video-specific validation (e.g., file size, duration)
                    const fileSize = card.headerFile.size / (1024 * 1024); // Convert to MB
                    if (fileSize > 16) { // WhatsApp typically limits videos to 16MB
                        setAlertObj({ 
                            active: true, 
                            type: "error", 
                            msg: `Video file for Card ${i+1} exceeds the maximum size of 16MB`
                        });
                        return;
                    }
                }
                
                if (!card.body || card.body.trim() === '') {
                    setAlertObj({ active: true, type: "error", msg: `Please add body text for Card ${i+1}` });
                    return;
                }
                if (card.buttons.length === 0) {
                    setAlertObj({ active: true, type: "error", msg: `Please add at least one button for Card ${i+1}` });
                    return;
                }
            }
        } else {
            // Original validation for non-carousel templates
            if (formData.headerType === 'image' || formData.headerType === 'video' || formData.headerType === 'document') {
                if (!formData.headerFile) {
                    setAlertObj({ active: true, type: "error", msg: "Please upload a media file" });
                    return;
                }
            }
            if (formData.body === '') {
                setAlertObj({ active: true, type: "error", msg: "Please enter a message body" });
                return;
            }
        }
    
        const data = { ...formData };
        data.sampleHeaderFields = (data.sampleHeaderFields.length === 0) ? "" : JSON.stringify(data.sampleHeaderFields);
        data.sampleBodyFields = (data.sampleBodyFields.length === 0) ? "" : JSON.stringify(data.sampleBodyFields);
        data.allButtons = (data.allButtons.length === 0) ? "" : JSON.stringify(data.allButtons);
    
        // process carousel main body sample fields
        data.carouselMainBodySampleFields = (data.carouselMainBodySampleFields?.length === 0) ? "" : JSON.stringify(data.carouselMainBodySampleFields);
        
        console.log("Payload: ", data);
    
        const token = await getValidToken();
        const tenant = localStorage.getItem('tenant');
        
        // Use FormData to handle file uploads properly
        const formDataObj = new FormData();
        
        // Add all regular fields to FormData
        for (const key in data) {
            if (key !== 'headerFile' && key !== 'carouselCards') {
                formDataObj.append(key, data[key]);
            }
        }
        
        // Handle carousel cards specially
        if (data.marketingOption === 'carousel') {
            // First, create a simplified version of cards without the file objects
            const simplifiedCards = data.carouselCards.map(card => {
                const { headerFile, ...rest } = card;
                return rest;
            });
            
            formDataObj.append('carouselCards', JSON.stringify(simplifiedCards));
            
            // Then append each card's file separately
            data.carouselCards.forEach((card, index) => {
                if (card.headerFile) {
                    console.log(`Appending file for card ${index}:`, card.headerFile.name);
                    formDataObj.append(`cardFile_${index}`, card.headerFile);
                }
            });
        } else if (data.headerFile) {
            // Handle regular template file
            formDataObj.append('headerFile', data.headerFile);
        }
        
        // Log the FormData entries for debugging
        for (let pair of formDataObj.entries()) {
            if (pair[0].startsWith('cardFile_') || pair[0] === 'headerFile') {
                console.log(`${pair[0]}: File object with name ${pair[1].name}`);
            } else {
                console.log(`${pair[0]}: ${typeof pair[1] === 'string' && pair[1].length > 100 ? pair[1].substring(0, 100) + '...' : pair[1]}`);
            }
        }
        
        axios.post(apiUrl, formDataObj, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'multipart/form-data',
                "X-Tenant-Id": tenant
            },
        })
        .then(response => {
            console.log('API Response:', response.data);
            if (response.status === 201) {
                handleSuccessAlert();
            } else {
                let message = response.data.message ? response.data.message : response.statusText;
                handleErrorAlert(response.status, message);
            }
        })
        .catch(error => {
            console.error('API Error:', error);
            setAlertObj({
                active: true,
                type: "error",
                msg: `Failed to submit form: ${error.response?.statusText || error.message}`,
                statusCode: error.response?.status || 500
            });
        });
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        const currentIndex = templateSteps.indexOf(templateOps);
        if (!isButtonsValid || error) {
            return;
        }
        if (currentIndex === templateSteps.length - 1) {
        // Submit the form data here
            setFormData({
                templateName,
                language,
                headerType,
                headerText,
                headerFile,
                sampleHeaderFields,
                body,
                sampleBodyFields,
                footer,
                marketingOption,
                templateType,
                allButtons,
                carouselCards,
                carouselMainBody,
                carouselMainBodySampleFields
            });
            handleSubmitData();
            console.log("Called the API");
        } else {
            setTemplateOps(templateSteps[currentIndex + 1]);
        }
    };

    const handleMarketingOptionChange = (event) => {
        setMarketingOption(event.target.value);
        setUtilityOption('');
    };

    const handleUtilityOptionChange = (event) => {
        setUtilityOption(event.target.value);
        setMarketingOption('');
    };

    const handlePrevious = () => {
        const currentIndex = templateSteps.indexOf(templateOps);
        if (currentIndex > 0) {
            setTemplateOps(templateSteps[currentIndex - 1]);
        }
    };

    useEffect(() => {
        let timeoutId;
        
        if (alertObj.active) {
        timeoutId = setTimeout(() => {
            handleAlertClose();
        }, 5000);
        }
        
        // Cleanup function to clear the timeout when component unmounts or when alertObj changes
        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [alertObj]);

    useEffect(() => {
        if (
            carouselCards?.length > 0 && 
            formData.marketingOption === 'carousel'
          ) {
            const headerTypeValue = carouselType === 'video' ? 'VIDEO' : 'IMAGE';
            
            const hasIncorrectTypes = carouselCards.some(
              card => card.headerType !== headerTypeValue
            );
            
            if (hasIncorrectTypes) {
              const updatedCards = carouselCards.map(card => ({
                ...card,
                headerType: headerTypeValue
              }));
              
              setCarouselCards(updatedCards);
            }
          }
    }, [carouselType, carouselCards]);

    return (
        <div sx={{ padding: 2 }}>
            
            <form onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: 2 }}>
                <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Grid container spacing={2} sx={{ marginBottom: 2 }}>
                        <Grid item>
                            <Button
                            variant={templateOps === 'setup-template' ? 'contained' : 'outlined'}
                            color="primary"
                            onClick={() => setTemplateOps('setup-template')}
                            sx={{ width: 200 }}
                            >
                                Set up template
                            </Button>
                        </Grid>
                        <Grid item>
                            <Button
                            variant={templateOps === 'edit-template' ? 'contained' : 'outlined'}
                            color="primary"
                            onClick={() => setTemplateOps('edit-template')}
                            sx={{ width: 200 }}
                            >
                                Edit template
                            </Button>
                        </Grid>
                    </Grid>
                </Grid>
                {templateOps === 'setup-template' && (
                    <Grid item xs={12}>
                    <Typography variant="h6" sx={{ marginBottom: 1 }}>
                        Set up your template
                    </Typography>
                        <RadioGroup
                            name="template-type"
                            value={templateType}
                            onChange={(event) => setTemplateType(event.target.value)}
                            row
                            >
                            <FormControlLabel value="marketing-template" control={<Radio color="primary" />} label="Marketing" />
                            <FormControlLabel value="utility-template" control={<Radio color="primary" />} label="Utility" />
                        </RadioGroup>
                        {templateType === 'marketing-template' && (
                            <Grid item xs={12}>
                                <Typography variant="h6" sx={{ marginBottom: 1 }}>
                                    Marketing
                                </Typography>
                                <RadioGroup
                                    aria-label="marketing-option"
                                    name="marketing-option"
                                    value={marketingOption}
                                    onChange={handleMarketingOptionChange}
                                    row
                                >
                                    <FormControlLabel value="custom" control={<Radio color="primary" />} label="Custom" />
                                    <FormControlLabel value="carousel" control={<Radio color="primary" />} label="Carousel" />
                                    {/* <FormControlLabel value="flows" control={<Radio color="primary" />} label="Flows" /> */}
                                </RadioGroup>
                                {marketingOption === 'custom' && (
                                    <Grid item xs={12}>
                                        <Typography variant="h6" sx={{ marginBottom: 1 }}>
                                            Click 'Next' to proceed
                                        </Typography>
                                    </Grid>
                                )}
                                {marketingOption === 'carousel' && (
                                    <Grid item xs={12}>
                                        <Typography variant="h6" sx={{ marginBottom: 1 }}>
                                            Select Carousel Type
                                        </Typography>
                                        <RadioGroup
                                            aria-label="carousel-type"
                                            name="carousel-type"
                                            value={carouselType}
                                            onChange={(event) => setCarouselType(event.target.value)}
                                            row
                                        >
                                            <FormControlLabel value="image" control={<Radio color="primary" />} label="Image Carousel" />
                                            <FormControlLabel value="video" control={<Radio color="primary" />} label="Video Carousel" />
                                        </RadioGroup>
                                        <Typography variant="h6" sx={{ marginBottom: 1, marginTop: 2 }}>
                                            Click 'Next' to proceed to create {carouselType.charAt(0).toUpperCase() + carouselType.slice(1)} Carousel Template
                                        </Typography>
                                    </Grid>
                                )}
                            </Grid>
                        )}
                        {templateType === 'utility-template' && (
                            <Grid item xs={12}>
                                <Typography variant="h6" sx={{ marginBottom: 1 }}>
                                    Utility
                                </Typography>
                                <RadioGroup
                                    aria-label="utility-option"
                                    name="utility-option"
                                    value={utilityOption}
                                    onChange={handleUtilityOptionChange}
                                    row
                                >
                                    <FormControlLabel value="custom" control={<Radio color="primary" />} label="Custom" />
                                    {/* <FormControlLabel value="flows" control={<Radio color="primary" />} label="Flows" /> */}
                                </RadioGroup>
                                {utilityOption === 'custom' && (
                                    <Grid item xs={12}>
                                        <Typography variant="h6" sx={{ marginBottom: 1 }}>
                                            Click 'Next' to proceed
                                        </Typography>
                                    </Grid>
                                )}
                            </Grid>
                        )}
                    </Grid>
                )}
                </Grid>
                {templateOps === 'edit-template' && (
                    (templateType === 'marketing-template' && marketingOption === 'custom') ||
                    (templateType === 'utility-template' && utilityOption === 'custom')
                ) && (
                    <>
                        <Grid item xs={12} md={2} style={{ display: 'flex', justifyContent: 'flex-start' }}>
                            <RegularTemplateEditor 
                                templateType={templateType}
                                templateName={templateName}
                                setTemplateName={setTemplateName}
                                language={language}
                                setLanguage={setLanguage}
                                headerType={headerType}
                                setHeaderType={setHeaderType}
                                headerText={headerText}
                                setHeaderText={setHeaderText}
                                headerFile={headerFile}
                                setHeaderFile={setHeaderFile}
                                body={body}
                                setBody={setBody}
                                sampleBodyFields={sampleBodyFields}
                                setsampleBodyFields={setsampleBodyFields}
                                sampleHeaderFields={sampleHeaderFields}
                                setsampleHeaderFields={setsampleHeaderFields}
                                footer={footer}
                                setFooter={setFooter}
                                templatesNamesData={templatesNamesData}
                                allButtons={allButtons}
                                setAllButtons={setAllButtons}
                                isButtonsValid={isButtonsValid}
                                setIsButtonsValid={setIsButtonsValid}
                                error={error}
                                setError={setError}
                                headerDuplicatePlaceholderError={headerDuplicatePlaceholderError}
                                setHeaderDuplicatePlaceholderError={setHeaderDuplicatePlaceholderError}
                                bodyDuplicatePlaceholderError={bodyDuplicatePlaceholderError}
                                setBodyDuplicatePlaceholderError={setBodyDuplicatePlaceholderError}
                            />
                            <Grid item xs={12} md={2} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <MessageNode
                                templateName={templateName}
                                language={language}
                                headerType={headerType}
                                headerText={headerText}
                                headerFile={headerFile}
                                body={body}
                                sampleBodyFields={sampleBodyFields}
                                sampleHeaderFields={sampleHeaderFields}
                                footer={footer}
                                allButtons={allButtons}
                                showSampleValues={false} // Use regular preview mode
                                mode="edit"
                            />
                            </Grid>
                        </Grid>
                    </>
                )}
                {templateOps === 'edit-template' && templateType === 'marketing-template' && marketingOption === 'carousel' && (
                    <>
                        <Grid item xs={12} md={2} style={{ display: 'flex', justifyContent: 'flex-start' }}>
                            <CarouselTemplateEditor 
                                templateName={templateName}
                                setTemplateName={setTemplateName}
                                language={language}
                                setLanguage={setLanguage}
                                cards={carouselCards}
                                setCards={setCarouselCards}
                                mainBody={carouselMainBody}
                                setMainBody={setCarouselMainBody}
                                mainBodySampleFields={carouselMainBodySampleFields}
                                setMainBodySampleFields={setCarouselMainBodySampleFields}
                                error={error}
                                setError={setError}
                                templatesNamesData={templatesNamesData}
                                carouselType={carouselType}
                                carouselMainBodyDuplicatePlaceholderError={carouselMainBodyDuplicatePlaceholderError}
                                setCarouselMainBodyDuplicatePlaceholderError={setCarouselMainBodyDuplicatePlaceholderError}
                                carouselCardBodyDuplicatePlaceholderError={carouselCardBodyDuplicatePlaceholderError}
                                setCarouselCardBodyDuplicatePlaceholderError={setCarouselCardBodyDuplicatePlaceholderError}
                            />
                            
                            {/* Also add carouselType prop to MessageNode */}
                            <Grid item xs={12} md={2} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <MessageNode
                                    templateName={templateName}
                                    language={language}
                                    headerType="none"
                                    headerText=""
                                    headerFile={null}
                                    body=""
                                    sampleBodyFields={[]}
                                    sampleHeaderFields={[]}
                                    footer=""
                                    allButtons={[]}
                                    showSampleValues={false}
                                    mode="edit"
                                    marketingOption="carousel"
                                    carouselCards={carouselCards}
                                    carouselMainBody={carouselMainBody}
                                    carouselMainBodySampleFields={carouselMainBodySampleFields}
                                    carouselType={carouselType}
                                    {...carouselCards.reduce((acc, card, index) => {
                                        if (card.headerFile) {
                                            acc[`cardFile_${index}`] = card.headerFile;
                                        }
                                        return acc;
                                    }, {})}
                                />
                            </Grid>
                        </Grid>
                    </>
                )}
                <Grid item xs={12}>
                    <Grid item xs={12}>
                        <Button type="button" variant="contained" color="primary" onClick={handlePrevious} sx={{ marginRight: 2 }}>
                            Previous
                        </Button>
                        <Button type="submit" variant="contained" color="primary">
                            {templateOps === 'edit-template' ? "Submit" : "Next"}
                        </Button>
                    </Grid>
                </Grid>
                <Grid item xs={12} sx={{ marginTop: 2 }}>
                    {alertObj.active && (
                        <Alert severity={alertObj.type} onClose={handleAlertClose}>
                        {alertObj.msg}
                        </Alert>
                    )}
                </Grid>
            </form>
        </div>
    );
};
export default SetupTemplate;
